import { Button } from "@win-reality/react-ui";

interface ClientPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  itemsPerPage: number;
  onPageChange: (page: number) => void;
  className?: string;
  itemLabel?: string;
}

export function ClientPagination({
  currentPage,
  totalPages,
  totalItems,
  itemsPerPage,
  onPageChange,
  className,
  itemLabel = "items",
}: ClientPaginationProps) {
  const canPreviousPage = currentPage > 0;
  const canNextPage = currentPage < totalPages - 1;

  const startItem = currentPage * itemsPerPage + 1;
  const endItem = Math.min((currentPage + 1) * itemsPerPage, totalItems);

  if (totalItems === 0) {
    return null;
  }

  return (
    <div
      className={`flex flex-col items-start gap-2 sm:flex-row sm:items-center sm:justify-between px-4 py-3 bg-gray-50 border-t border-gray-200 ${className || ""}`}
    >
      {/* Left side - Count display */}
      <div className="text-sm text-muted-foreground">
        Total: {totalItems} {itemLabel} | Showing {startItem}-{endItem}
      </div>

      {/* Right side - Page navigation (stacks under count on small screens) */}
      <div className="flex items-center gap-2 mt-5 sm:mt-0 sm:justify-end">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(Math.max(0, currentPage - 1))}
          isDisabled={!canPreviousPage}
          className="h-9"
        >
          Previous
        </Button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {/* First page */}
          <Button
            variant={currentPage === 0 ? "secondary-flat-light" : "ghost"}
            size="sm"
            onClick={() => onPageChange(0)}
            className="w-9 h-9"
          >
            1
          </Button>

          {/* Left ellipsis if current page is far from start */}
          {currentPage > 2 && totalPages > 3 && (
            <span className="px-2 text-muted-foreground">...</span>
          )}

          {/* Show page before current (when on middle pages or last page) */}
          {currentPage > 1 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(currentPage - 1)}
              className="w-9 h-9"
            >
              {currentPage}
            </Button>
          )}

          {/* Current page if not first or last */}
          {currentPage > 0 && currentPage < totalPages - 1 && (
            <Button
              variant="secondary-flat-light"
              size="sm"
              onClick={() => onPageChange(currentPage)}
              className="w-9 h-9"
            >
              {currentPage + 1}
            </Button>
          )}

          {/* Show page after current (when on middle pages or first page) */}
          {currentPage < totalPages - 2 && totalPages > 2 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onPageChange(currentPage + 1)}
              className="w-9 h-9"
            >
              {currentPage + 2}
            </Button>
          )}

          {/* Right ellipsis if current page is far from end */}
          {currentPage < totalPages - 3 && totalPages > 3 && (
            <span className="px-2 text-muted-foreground">...</span>
          )}

          {/* Last page if more than 1 page */}
          {totalPages > 1 && (
            <Button
              variant={
                currentPage === totalPages - 1
                  ? "secondary-flat-light"
                  : "ghost"
              }
              size="sm"
              onClick={() => onPageChange(totalPages - 1)}
              className="w-9 h-9"
            >
              {totalPages}
            </Button>
          )}
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPageChange(currentPage + 1)}
          isDisabled={!canNextPage}
          className="h-9"
        >
          Next
        </Button>
      </div>
    </div>
  );
}
